/**
 * @fileoverview Walkthrough Tooltip
 * @description Floating tooltip that displays step content during guided tours.
 * Positions itself relative to the highlighted target element.
 * Falls back to centered display when no target is specified.
 * @module features/walkthrough/components/WalkthroughTooltip
 */

import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import {
  ChevronLeft,
  ChevronRight,
  X,
  Lightbulb,
  CheckCircle2,
} from 'lucide-react';
import { cn } from '@flowstate/utils';
import type { WalkthroughStep, TooltipPosition } from '../types';

interface WalkthroughTooltipProps {
  /** The current step to display */
  step: WalkthroughStep;
  /** Current step index (0-based) */
  stepIndex: number;
  /** Total number of steps */
  totalSteps: number;
  /** Handler for next step */
  onNext: () => void;
  /** Handler for previous step */
  onPrev: () => void;
  /** Handler for dismissing the walkthrough */
  onDismiss: () => void;
  /** Whether this is the last step */
  isLastStep: boolean;
  /** Whether this is the first step */
  isFirstStep: boolean;
  /** Additional class names */
  className?: string;
}

interface Position {
  top?: number | string;
  left?: number | string;
  right?: number | string;
  bottom?: number | string;
  transform?: string;
}

function calculatePosition(
  targetSelector?: string,
  placement?: TooltipPosition
): Position {
  if (!targetSelector) {
    // Center on screen for modal-style steps
    return {
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    };
  }

  const element = document.querySelector(targetSelector);
  if (!element) {
    return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
  }

  const rect = element.getBoundingClientRect();
  const tooltipGap = 16;

  switch (placement) {
    case 'top':
      return {
        bottom: window.innerHeight - rect.top + tooltipGap,
        left: rect.left + rect.width / 2,
        transform: 'translateX(-50%)',
      };
    case 'bottom':
      return {
        top: rect.bottom + tooltipGap,
        left: rect.left + rect.width / 2,
        transform: 'translateX(-50%)',
      };
    case 'left':
      return {
        top: rect.top + rect.height / 2,
        right: window.innerWidth - rect.left + tooltipGap,
        transform: 'translateY(-50%)',
      };
    case 'right':
      return {
        top: rect.top + rect.height / 2,
        left: rect.right + tooltipGap,
        transform: 'translateY(-50%)',
      };
    default:
      return {
        top: rect.bottom + tooltipGap,
        left: rect.left + rect.width / 2,
        transform: 'translateX(-50%)',
      };
  }
}

export function WalkthroughTooltip({
  step,
  stepIndex,
  totalSteps,
  onNext,
  onPrev,
  onDismiss,
  isLastStep,
  isFirstStep,
  className,
}: WalkthroughTooltipProps) {
  const [position, setPosition] = useState<Position>({});

  const updatePosition = useCallback(() => {
    setPosition(calculatePosition(step.target, step.position));
  }, [step.target, step.position]);

  useEffect(() => {
    updatePosition();

    const handleUpdate = () => updatePosition();
    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);

    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
    };
  }, [updatePosition]);

  // Scroll target into view if needed
  useEffect(() => {
    if (!step.target) {return;}
    const element = document.querySelector(step.target);
    if (!element) {return;}

    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Recalculate after scroll
    const timer = setTimeout(updatePosition, 350);
    return () => clearTimeout(timer);
  }, [step.target, updatePosition]);

  const progressPercent = ((stepIndex + 1) / totalSteps) * 100;

  return (
    <div
      className={cn('fixed z-[9999]', className)}
      style={position as React.CSSProperties}
      role="dialog"
      aria-label={`Walkthrough step ${stepIndex + 1} of ${totalSteps}: ${step.title}`}
    >
      <Card className="w-[360px] shadow-xl border-primary/20" variant="elevated">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                {stepIndex + 1}
              </div>
              <CardTitle className="text-base">{step.title}</CardTitle>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={onDismiss}
              aria-label="Dismiss walkthrough"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <Progress value={progressPercent} className="h-1.5 mt-2" />
          <p className="text-xs text-muted-foreground mt-1">
            Step {stepIndex + 1} of {totalSteps}
          </p>
        </CardHeader>

        <CardContent className="pb-3">
          <p className="text-sm text-muted-foreground">{step.description}</p>

          {step.bestPractice && (
            <div className="mt-3 flex items-start gap-2 rounded-md bg-primary/5 p-2.5 border border-primary/10">
              <Lightbulb className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <p className="text-xs text-primary/90">{step.bestPractice}</p>
            </div>
          )}
        </CardContent>

        <CardFooter className="flex items-center justify-between pt-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={onPrev}
            disabled={isFirstStep}
            className="gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </Button>

          <Button size="sm" onClick={onNext} className="gap-1">
            {isLastStep ? (
              <>
                <CheckCircle2 className="h-4 w-4" />
                {step.actionLabel || 'Finish'}
              </>
            ) : (
              <>
                {step.actionLabel || 'Next'}
                <ChevronRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
